import React, { useState, useMemo, useEffect } from 'react';
import { Button, Modal, Form, Input, DatePicker, InputNumber, Table, Space, Tag, Switch, Card, Typography, Row, Col, Upload, Avatar, Select, Drawer, Badge, Statistic, Tooltip, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, UploadOutlined, CameraOutlined, UserOutlined, CheckOutlined, CloseOutlined, EyeOutlined, ClockCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import MDEditor from '@uiw/react-md-editor';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import apiService from '../../../services/api';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import { BootcampResponse, CreateBootcampRequest, UpdateBootcampRequest, BootcampApplicationResponse, UpdateBootcampApplicationRequest } from '../../../types';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

const BOOTCAMP_STATUSES = [
  { value: 'Draft', label: 'Taslak', color: 'default' },
  { value: 'Published', label: 'Yayınlandı', color: 'blue' },
  { value: 'Started', label: 'Başladı', color: 'green' },
  { value: 'Completed', label: 'Tamamlandı', color: 'purple' },
  { value: 'Cancelled', label: 'İptal Edildi', color: 'red' }
];

function isBootcampTextEmpty(s: string): boolean {
  const t = s
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/[#*\-_>[\]]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return t.length === 0;
}

const BootcampsTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBootcamp, setEditingBootcamp] = useState<BootcampResponse | null>(null);
  const [form] = Form.useForm();
  const [description, setDescription] = useState<string>('');
  const [requirements, setRequirements] = useState<string>('');
  const [applicationsDrawerVisible, setApplicationsDrawerVisible] = useState(false);
  const [selectedBootcamp, setSelectedBootcamp] = useState<BootcampResponse | null>(null);
  const [applicationModalVisible, setApplicationModalVisible] = useState(false);
  const [editingApplication, setEditingApplication] = useState<BootcampApplicationResponse | null>(null);
  const [applicationForm] = Form.useForm();
  const [applicationPage, setApplicationPage] = useState(1);
  const applicationPageSize = 20;

  // Reset page when selectedBootcamp changes
  useEffect(() => {
    setApplicationPage(1);
  }, [selectedBootcamp?.id]);

  const { data: categoriesData } = useQuery({
    queryKey: mentorProfileQueryKeys.categories,
    queryFn: async () => {
      const res = await apiService.getCategories();
      if (!res.success) throw new Error(res.message);
      return res.data;
    },
  });
  const categories = useMemo(() => (categoriesData ?? []).map(c => ({ id: c.id, name: c.name })), [categoriesData]);

  const { data: bootcamps = [], isLoading: loading } = useQuery({
    queryKey: mentorProfileQueryKeys.bootcamps,
    queryFn: async () => {
      const res = await apiService.getBootcamps();
      if (!res.success) throw new Error(res.message || 'Bootcampler yüklenemedi');
      return res.data;
    },
  });

  const { data: applicationsResult, isLoading: applicationsLoading } = useQuery({
    queryKey: [...mentorProfileQueryKeys.bootcampApplications(selectedBootcamp?.id ?? 0), applicationPage],
    queryFn: async () => {
      if (!selectedBootcamp) return null;
      const res = await apiService.getBootcampApplications(selectedBootcamp.id, applicationPage, applicationPageSize);
      if (!res.success) throw new Error(res.message || 'Başvurular yüklenemedi.');
      return res.data;
    },
    enabled: !!selectedBootcamp?.id,
  });
  const applications = applicationsResult?.data ?? [];
  const applicationTotal = applicationsResult?.totalCount ?? 0;

  const createBootcampMutation = useMutation({
    mutationFn: (data: CreateBootcampRequest) => apiService.createBootcamp(data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Bootcamp başarıyla eklendi.');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.bootcamps });
        setModalVisible(false);
      } else toast.error(res.message || 'Bootcamp eklenemedi.');
    },
    onError: () => toast.error('Bootcampler yüklenirken bir hata oluştu'),
  });

  const updateBootcampMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBootcampRequest }) => apiService.updateBootcamp(id, data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Bootcamp başarıyla güncellendi.');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.bootcamps });
        setModalVisible(false);
      } else toast.error(res.message || 'Bootcamp güncellenemedi.');
    },
    onError: () => toast.error('Bootcamp güncellenirken bir hata oluştu'),
  });

  const deleteBootcampMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteBootcamp(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Bootcamp başarıyla silindi.');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.bootcamps });
      } else toast.error(res.message || 'Bootcamp silinemedi.');
    },
    onError: () => toast.error('Bootcamp silinirken bir hata oluştu'),
  });

  const uploadImageMutation = useMutation({
    mutationFn: ({ bootcampId, file }: { bootcampId: number; file: File }) => apiService.uploadBootcampImage(bootcampId, file),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Fotoğraf yüklendi.');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.bootcamps });
      } else toast.error(res.message || 'Fotoğraf yüklenemedi.');
    },
    onError: () => toast.error('Fotoğraf yüklenirken bir hata oluştu'),
  });

  const updateApplicationMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBootcampApplicationRequest; bootcampId: number }) => apiService.updateBootcampApplication(id, data),
    onSuccess: (res, { bootcampId, data }) => {
      if (res.success) {
        // Key artık [...bootcampApplications(bootcampId), page] → prefix ile eşleşir
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.bootcampApplications(bootcampId) });
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.bootcamps });
        setApplicationModalVisible(false);
        setEditingApplication(null);
        applicationForm.resetFields();
        const statusMessage = data.status === 'Approved' ? 'Başvuru onaylandı.' : 
                             data.status === 'Rejected' ? 'Başvuru reddedildi.' : 
                             'Başvuru güncellendi.';
        toast.success(statusMessage);
      } else toast.error(res.message || 'Başvuru güncellenemedi.');
    },
    onError: () => toast.error('Başvuru güncellenirken bir hata oluştu'),
  });

  const handleAdd = () => {
    setEditingBootcamp(null);
    setDescription('');
    setRequirements('');
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (bootcamp: BootcampResponse) => {
    setEditingBootcamp(bootcamp);
    setDescription(bootcamp.description || '');
    setRequirements(bootcamp.requirements || '');
    form.setFieldsValue({
      title: bootcamp.title,
      range: [dayjs(bootcamp.startDate), dayjs(bootcamp.endDate)],
      maxParticipants: bootcamp.maxParticipants,
      price: bootcamp.price,
      status: bootcamp.status,
      isOnline: bootcamp.isOnline,
      meetingLink: bootcamp.meetingLink,
      categoryId: bootcamp.categoryId
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (isBootcampTextEmpty(description)) {
        toast.error('Açıklama alanı zorunludur. Lütfen içerik girin.');
        return;
      }
      if (isBootcampTextEmpty(requirements)) {
        toast.error('Gereksinimler alanı zorunludur. Lütfen içerik girin.');
        return;
      }
      const categoryId = values.categoryId;
      if (categoryId == null || (typeof categoryId === 'number' && categoryId < 1)) {
        toast.error('Kategori seçmelisiniz.');
        return;
      }
      const bootcampData: CreateBootcampRequest = {
        title: values.title,
        description: description,
        requirements: requirements,
        startDate: values.range[0].format('YYYY-MM-DD'),
        endDate: values.range[1].format('YYYY-MM-DD'),
        maxParticipants: values.maxParticipants,
        price: values.price ?? 0,
        status: values.status,
        location: values.location,
        isOnline: values.isOnline || false,
        meetingLink: values.meetingLink,
        categoryId: categoryId as number
      };

      if (editingBootcamp) {
        updateBootcampMutation.mutate({ id: editingBootcamp.id, data: bootcampData });
      } else {
        createBootcampMutation.mutate(bootcampData);
      }
    } catch {
      // form validation
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingBootcamp(null);
    form.resetFields();
    setDescription('');
    setRequirements('');
  };

  const handleDelete = (id: number) => deleteBootcampMutation.mutate(id);

  const handleImageUpload = (file: File, bootcampId: number) => {
    uploadImageMutation.mutate({ bootcampId, file });
  };

  const getImageUrl = (image?: string) => {
    if (!image) return null;
    return `data:image/jpeg;base64,${image}`;
  };

  const handleViewApplications = (bootcamp: BootcampResponse) => {
    setSelectedBootcamp(bootcamp);
    setApplicationsDrawerVisible(true);
  };

  const handleReviewApplication = (application: BootcampApplicationResponse) => {
    setEditingApplication(application);
    applicationForm.setFieldsValue({
      status: application.status,
      mentorNotes: application.mentorNotes
    });
    setApplicationModalVisible(true);
  };

  const handleApplicationModalOk = async () => {
    if (editingApplication && editingApplication.status === 'Completed') {
      setApplicationModalVisible(false);
      setEditingApplication(null);
      applicationForm.resetFields();
      return;
    }
    try {
      const values = await applicationForm.validateFields();
      if (editingApplication) {
        updateApplicationMutation.mutate({
          id: editingApplication.id,
          data: values,
          bootcampId: editingApplication.bootcampId,
        });
      }
    } catch {
      // form validation
    }
  };

  const handleApplicationModalCancel = () => {
    setApplicationModalVisible(false);
    setEditingApplication(null);
    applicationForm.resetFields();
  };

  const handleApproveApplication = (application: BootcampApplicationResponse) => {
    if (application.status === 'Completed') return;
    updateApplicationMutation.mutate({
      id: application.id,
      data: { status: 'Approved', mentorNotes: '' },
      bootcampId: application.bootcampId,
    });
  };

  const handleRejectApplication = (application: BootcampApplicationResponse) => {
    if (application.status === 'Completed') return;
    updateApplicationMutation.mutate({
      id: application.id,
      data: { status: 'Rejected', mentorNotes: '' },
      bootcampId: application.bootcampId,
    });
  };

  // Filtreleme
  const filteredBootcamps = bootcamps.filter(bootcamp =>
    bootcamp.title.toLowerCase().includes(searchText.toLowerCase()) ||
    bootcamp.status.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Fotoğraf',
      dataIndex: 'image',
      key: 'image',
      width: 80,
      render: (image: string, record: BootcampResponse) => {
        const imageUrl = getImageUrl(image);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Avatar 
              size={40} 
              src={imageUrl} 
              icon={<CameraOutlined />}
              style={{ backgroundColor: '#f0f0f0' }}
            />
            <Upload
              showUploadList={false}
              beforeUpload={(file) => {
                handleImageUpload(file, record.id);
                return false;
              }}
              accept="image/*"
            >
              <Button 
                type="text" 
                size="small" 
                icon={<UploadOutlined />}
                loading={uploadImageMutation.isPending}
                style={{ color: 'var(--primary-500)' }}
              />
            </Upload>
          </div>
        );
      },
    },
    {
      title: 'Başlık',
      dataIndex: 'title',
      key: 'title',
      sorter: (a: BootcampResponse, b: BootcampResponse) => a.title.localeCompare(b.title),
    },
    {
      title: 'Kategori',
      dataIndex: 'categoryName',
      key: 'categoryName',
      render: (categoryName: string) => categoryName || '-',
    },
    {
      title: 'Tarih Aralığı',
      key: 'date',
      render: (_: any, record: BootcampResponse) => 
        `${dayjs(record.startDate).format('DD.MM.YYYY')} - ${dayjs(record.endDate).format('DD.MM.YYYY')}`,
      sorter: (a: BootcampResponse, b: BootcampResponse) => 
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    },
    {
      title: 'Katılımcı Sayısı',
      dataIndex: 'maxParticipants',
      key: 'maxParticipants',
      render: (max: number, record: BootcampResponse) => `${record.currentParticipants}/${max}`,
      sorter: (a: BootcampResponse, b: BootcampResponse) => a.maxParticipants - b.maxParticipants,
    },
    {
      title: 'Ücret (₺)',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `${price}₺`,
      sorter: (a: BootcampResponse, b: BootcampResponse) => a.price - b.price,
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = BOOTCAMP_STATUSES.find(s => s.value === status);
        return <Tag color={statusConfig?.color || 'default'}>{statusConfig?.label || status}</Tag>;
      },
      filters: BOOTCAMP_STATUSES.map(status => ({ text: status.label, value: status.value })),
      onFilter: (value: boolean | React.Key, record: BootcampResponse) => record.status === value,
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: BootcampResponse) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: 'var(--text-secondary)' }}
          >
            Düzenle
          </Button>
          <Button
            type="text"
            icon={<UserOutlined />}
            onClick={() => handleViewApplications(record)}
            style={{ color: 'var(--primary-500)' }}
          >
            <Badge count={record.applicationCount} size="small">
              Başvurular
            </Badge>
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PanelTabCard
      title="Bootcampler"
      description="Bootcamp oluşturun, düzenleyin ve başvuruları yönetin."
    >
      <TabListLoading loading={loading}>
      <Card style={{ marginBottom: '16px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                backgroundColor: 'var(--text-secondary)',
                borderColor: 'var(--text-secondary)',
                fontWeight: 500
              }}
            >
              Yeni Bootcamp
            </Button>
          </Col>
        </Row>
        
        <Row style={{ marginBottom: '16px' }}>
          <Col span={8}>
            <Input
              placeholder="Başlık, durum veya lokasyon ara..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ borderRadius: '6px' }}
            />
          </Col>
        </Row>

        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={filteredBootcamps}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} bootcamp`,
          }}
        />
      </Card>
      </TabListLoading>

      <Modal
        title={editingBootcamp ? 'Bootcamp Düzenle' : 'Yeni Bootcamp'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={900}
        okText="Kaydet"
        cancelText="İptal"
        okButtonProps={{
          style: {
            backgroundColor: 'var(--text-secondary)',
            borderColor: 'var(--text-secondary)',
            color: 'white',
            fontWeight: 500
          }
        }}
        cancelButtonProps={{
          style: {
            backgroundColor: 'white',
            borderColor: '#d1d5db',
            color: 'var(--text-secondary)',
            fontWeight: 500
          }
        }}
      >
        <Form layout="vertical" form={form} requiredMark>
          <Form.Item 
            name="title" 
            label="Başlık" 
            rules={[{ required: true, message: 'Başlık gerekli' }]}
          >
            <Input placeholder="Bootcamp başlığı" />
          </Form.Item>

          <Form.Item label="Açıklama" required tooltip="Zorunlu. Markdown veya açıklama metni">
            <MDEditor
              value={description}
              onChange={(val) => setDescription(val || '')}
              data-color-mode="light"
              height={200}
            />
          </Form.Item>

          <Form.Item label="Gereksinimler" required tooltip="Zorunlu. Katılım koşullarını girin">
            <MDEditor
              value={requirements}
              onChange={(val) => setRequirements(val || '')}
              data-color-mode="light"
              height={200}
            />
          </Form.Item>

          <Form.Item 
            name="range" 
            label="Tarih aralığı" 
            rules={[{ required: true, message: 'Tarih seçin' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
          <Form.Item 
            name="maxParticipants" 
            label="Maksimum katılımcı" 
            initialValue={10}
            rules={[
              { required: true, message: 'Katılımcı sayısı gerekli' },
              { type: 'number', min: 1, message: 'En az 1 katılımcı' }
            ]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="price" 
                label="Ücret (₺)" 
                initialValue={0}
                rules={[
                  { required: true, message: 'Ücret alanı doldurulmalı' },
                  { type: 'number', min: 0, message: '0 (ücretsiz) veya pozitif' }
                ]}
              >
                <InputNumber min={0} step={50} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            name="status" 
            label="Durum" 
            initialValue="Draft"
            rules={[{ required: true, message: 'Durum seçin' }]}
          >
            <Select placeholder="Durum seçin">
              {BOOTCAMP_STATUSES.map(status => (
                <Option key={status.value} value={status.value}>
                  {status.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="categoryId"
            label="Kategori"
            rules={[{ required: true, message: 'Kategori seçin' }]}
          >
            <Select placeholder="Kategori seçin">
              {categories.map(category => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="isOnline" label="Online Bootcamp" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="location" label="Lokasyon">
            <Input placeholder="Fiziksel adres veya açıklama" />
          </Form.Item>

          <Form.Item name="meetingLink" label="Toplantı Bağlantısı">
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Başvuru Drawer */}
      <Drawer
        title={`${selectedBootcamp?.title} - Başvurular`}
        placement="right"
        width={900}
        open={applicationsDrawerVisible}
        onClose={() => setApplicationsDrawerVisible(false)}
      >
        {/* İstatistikler */}
        {selectedBootcamp && (
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Toplam Başvuru"
                  value={applications.length}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Bekleyen"
                  value={applications.filter(a => a.status === 'Pending').length}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Onaylanan"
                  value={applications.filter(a => a.status === 'Approved').length}
                  prefix={<CheckOutlined />}
                  valueStyle={{ color: 'var(--success)' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Reddedilen"
                  value={applications.filter(a => a.status === 'Rejected').length}
                  prefix={<CloseOutlined />}
                  valueStyle={{ color: 'var(--error)' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        <Table
          scroll={{ x: 'max-content' }}
          dataSource={applications}
          rowKey="id"
          pagination={{
            current: applicationPage,
            pageSize: applicationPageSize,
            total: applicationTotal,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} başvuru`,
            onChange: (newPage, newPageSize) => {
              setApplicationPage(newPage);
            },
            onShowSizeChange: (current, size) => {
              setApplicationPage(1);
            },
          }}
          columns={[
            {
              title: 'Katılımcı',
              key: 'mentee',
              width: 200,
              render: (_, record: BootcampApplicationResponse) => {
                const menteeName = record.menteeName || (record.mentee ? `${record.mentee.firstName} ${record.mentee.lastName}` : '');
                const menteeEmail = record.menteeEmail || record.mentee?.email || '';
                return (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      size={40} 
                      icon={<UserOutlined />}
                      style={{ backgroundColor: '#722ed1', marginRight: '12px' }}
                    />
                    <div>
                      <div style={{ fontWeight: '500' }}>
                        {menteeName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {menteeEmail}
                      </div>
                    </div>
                  </div>
                );
              },
            },
            {
              title: 'Başvuru Tarihi',
              dataIndex: 'appliedAt',
              key: 'appliedAt',
              width: 150,
              render: (date: string) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarOutlined style={{ marginRight: '4px', color: 'var(--primary-500)' }} />
                  <Text>{dayjs(date).format('DD.MM.YYYY HH:mm')}</Text>
                </div>
              ),
              sorter: (a: BootcampApplicationResponse, b: BootcampApplicationResponse) => 
                new Date(a.appliedAt).getTime() - new Date(b.appliedAt).getTime(),
            },
            {
              title: 'Durum',
              dataIndex: 'status',
              key: 'status',
              width: 120,
              render: (status: string) => {
                const statusConfig = {
                  'Pending': { color: 'orange', text: 'Beklemede' },
                  'Approved': { color: 'green', text: 'Onaylandı' },
                  'Rejected': { color: 'red', text: 'Reddedildi' },
                  'Completed': { color: 'blue', text: 'Tamamlandı' }
                };
                const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
                return <Tag color={config.color}>{config.text}</Tag>;
              },
              filters: [
                { text: 'Beklemede', value: 'Pending' },
                { text: 'Onaylandı', value: 'Approved' },
                { text: 'Reddedildi', value: 'Rejected' },
                { text: 'Tamamlandı', value: 'Completed' }
              ],
              onFilter: (value: boolean | React.Key, record: BootcampApplicationResponse) => record.status === value,
            },
            {
              title: 'Değerlendirme',
              key: 'review',
              width: 150,
              render: (_, record: BootcampApplicationResponse) => (
                <div>
                  {record.isReviewMentee && (
                    <Tag color="green" style={{ marginBottom: '4px' }}>Mentee Değerlendirdi</Tag>
                  )}
                  {record.isReviewMentor && (
                    <Tag color="blue" style={{ marginBottom: '4px' }}>Mentor Değerlendirdi</Tag>
                  )}
                  {!record.isReviewMentee && !record.isReviewMentor && (
                    <Tag color="orange">Beklemede</Tag>
                  )}
                </div>
              )
            },
            {
              title: 'İşlemler',
              key: 'actions',
              width: 180,
              render: (_: any, record: BootcampApplicationResponse) => (
                <Space>
                  <Tooltip title="Detayları Görüntüle">
                    <Button 
                      type="text" 
                      icon={<EyeOutlined />}
                      onClick={() => handleReviewApplication(record)}
                      style={{ color: 'var(--primary-500)' }}
                    />
                  </Tooltip>
                  {record.status !== 'Completed' && (
                    <Space.Compact>
                      {record.status !== 'Approved' && (
                        <Tooltip title="Onayla">
                          <Button 
                            type="text" 
                            icon={<CheckOutlined />}
                            onClick={() => handleApproveApplication(record)}
                            style={{ color: 'var(--success)' }}
                            loading={updateApplicationMutation.isPending}
                          />
                        </Tooltip>
                      )}
                      {record.status !== 'Rejected' && (
                        <Tooltip title="Reddet">
                          <Button 
                            type="text" 
                            danger
                            icon={<CloseOutlined />}
                            onClick={() => handleRejectApplication(record)}
                            loading={updateApplicationMutation.isPending}
                          />
                        </Tooltip>
                      )}
                    </Space.Compact>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Drawer>

      {/* Başvuru İnceleme Modal */}
      <Modal
        title="Başvuru Detayları"
        open={applicationModalVisible}
        onOk={handleApplicationModalOk}
        onCancel={handleApplicationModalCancel}
        width={700}
        okText={editingApplication && editingApplication.status === 'Completed' ? 'Güncelle' : 'Güncelle'}
        cancelText="İptal"
        okButtonProps={{
          style: { backgroundColor: 'var(--text-secondary)', borderColor: 'var(--text-secondary)' },
          disabled: editingApplication?.status === 'Completed'
        }}
      >
        {editingApplication && (
          <div>
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <Avatar 
                    size={60} 
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#722ed1', marginRight: '16px' }}
                  />
                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      {editingApplication.menteeName}
                    </Title>
                    <Text type="secondary">{editingApplication.menteeEmail}</Text>
                  </div>
                </div>
              </Col>
              
              <Col span={12}>
                <Text strong>Başvuru Tarihi:</Text>
                <br />
                <Text>{dayjs(editingApplication.appliedAt).format('DD.MM.YYYY HH:mm')}</Text>
              </Col>
              <Col span={12}>
                <Text strong>Durum:</Text>
                <br />
                <Tag color={
                  editingApplication.status === 'Pending' ? 'orange' :
                  editingApplication.status === 'Approved' ? 'green' :
                  editingApplication.status === 'Rejected' ? 'red' : 'blue'
                }>
                  {editingApplication.status === 'Pending' ? 'Beklemede' :
                   editingApplication.status === 'Approved' ? 'Onaylandı' :
                   editingApplication.status === 'Rejected' ? 'Reddedildi' : 'Tamamlandı'}
                </Tag>
              </Col>
              
              {editingApplication.coverLetter && (
                <Col span={24}>
                  <Text strong>Başvuru Mektubu:</Text>
                  <div style={{ 
                    background: 'var(--bg-tertiary)', 
                    padding: '12px', 
                    borderRadius: '6px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    marginTop: '8px'
                  }}>
                    {editingApplication.coverLetter}
                  </div>
                </Col>
              )}
              
              {editingApplication.mentorNotes && (
                <Col span={24}>
                  <Text strong>Mentor Notları:</Text>
                  <div style={{ 
                    background: '#f0f9ff', 
                    padding: '12px', 
                    borderRadius: '6px',
                    marginTop: '8px'
                  }}>
                    {editingApplication.mentorNotes}
                  </div>
                </Col>
              )}
              
              <Col span={24}>
                <Text strong>Değerlendirme Durumu:</Text>
                <br />
                <Space style={{ marginTop: '8px' }}>
                  {editingApplication.isReviewMentee && (
                    <Tag color="green">Mentee Değerlendirdi</Tag>
                  )}
                  {editingApplication.isReviewMentor && (
                    <Tag color="blue">Mentor Değerlendirdi</Tag>
                  )}
                  {!editingApplication.isReviewMentee && !editingApplication.isReviewMentor && (
                    <Tag color="orange">Beklemede</Tag>
                  )}
                </Space>
              </Col>
            </Row>

            <Divider />

            <Form layout="vertical" form={applicationForm}>
              {editingApplication.status !== 'Completed' ? (
                <Form.Item
                  name="status"
                  label="Durum"
                  rules={[{ required: true, message: 'Durum seçin' }]}
                >
                  <Select placeholder="Durum seçin">
                    <Option value="Pending">Beklemede</Option>
                    <Option value="Approved">Onayla</Option>
                    <Option value="Rejected">Reddet</Option>
                  </Select>
                </Form.Item>
              ) : (
                <Form.Item label="Durum">
                  <Text type="secondary">Bu başvuru tamamlandı olarak işaretlenmiş. Durum değiştirilemez.</Text>
                </Form.Item>
              )}

              <Form.Item
                name="mentorNotes"
                label="Mentor Notları"
              >
                <Input.TextArea 
                  rows={4} 
                  placeholder="Katılımcı hakkında notlarınızı yazın..."
                  disabled={editingApplication.status === 'Completed'}
                />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </PanelTabCard>
  );
};

export default BootcampsTab;


